// src/pages/Privacy.js
// Privacy Policy Page

import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../components/common';
import './Upgrade.css';

function Privacy() {

  return (
    <div className="upgrade-container">
      <SEOHead
        title="Privacy Policy | DriftAlarm"
        description="DriftAlarm Privacy Policy covering data collection, AI processing, scan data retention, GDPR and CCPA compliance, and third-party services for our EASM platform."
        path="/privacy"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Privacy Policy — DriftAlarm",
            "url": "https://www.driftalarm.com/privacy",
            "dateModified": "2026-03-13T00:00:00-05:00",
            "publisher": {
              "@type": "Organization",
              "name": "DriftAlarm",
              "url": "https://www.driftalarm.com",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.driftalarm.com/images/og-image.png"
              },
              "foundingDate": "2026",
              "sameAs": ["https://www.driftalarm.com"]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
              { "@type": "ListItem", "position": 2, "name": "Privacy Policy" }
            ]
          }
        ]}
      />
      <PublicHeader activeLink={null} />

      {/* Main Content */}
      <div className="upgrade-content legal-content">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last Updated: March 2026</p>

        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            DriftAlarm ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy
            explains how we collect, use, store, and protect your information when you use the DriftAlarm
            platform at <a href="https://www.driftalarm.com" target="_blank" rel="noopener noreferrer">www.driftalarm.com</a>.
            This policy applies to all users of the Service. Effective date: March 2026.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Information We Collect</h2>

          <h3>2a. Account Information</h3>
          <ul>
            <li>Email address (from Microsoft Azure AD External ID authentication)</li>
            <li>Display name</li>
            <li>Organization name (if provided)</li>
            <li>User ID (assigned by authentication provider)</li>
            <li>Tier and subscription status</li>
            <li>Activation milestones (first asset added, first scan completed, alerts configured)</li>
          </ul>

          <h3>2b. Scan Target Information</h3>
          <ul>
            <li>Domain names and IP addresses/ranges you submit for scanning</li>
            <li>Scan results including discovered vulnerabilities, open ports, SSL certificates, DNS records, subdomains</li>
            <li>Asset inventory data (subdomains, IPs, ports, URLs, technologies)</li>
            <li>Drift detection data (baselines, change events, alarm configurations)</li>
            <li>Deep security scan results (DAST findings, SSL/TLS analysis, web server analysis)</li>
          </ul>

          <h3>2c. AI Processing Data</h3>
          <ul>
            <li>Scan findings sent to Anthropic's Claude API for remediation guidance, risk scoring, and report generation</li>
            <li>Technology stack data sent for AI analysis</li>
            <li>AI-generated content (remediation advice, executive reports, risk assessments)</li>
            <li>Anthropic's commercial API does not use inputs/outputs for model training</li>
          </ul>

          <h3>2d. API Key Data</h3>
          <ul>
            <li>API key names and scopes (user-defined)</li>
            <li>API key hashes (SHA-256 — full key never stored)</li>
            <li>API key usage: request count, last used timestamp, last used IP address</li>
          </ul>

          <h3>2e. Analytics and Usage Data</h3>
          <ul>
            <li>Google Analytics data (page views, session duration, feature usage)</li>
            <li>Activation tracking events (onboarding wizard completion, first scan, alert configuration)</li>
            <li>Feature usage patterns (scan types used, reports generated, tools accessed)</li>
            <li>Browser type, device type, and IP address</li>
          </ul>

          <h3>2f. Communication Data</h3>
          <ul>
            <li>Onboarding email engagement (5-email drip sequence for trial users)</li>
            <li>Notification channel configurations (email addresses, Slack webhook URLs, generic webhook URLs)</li>
            <li>Email opt-out preferences</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. How We Use Your Information</h2>
          <ul>
            <li>Provide and operate the scanning and monitoring services</li>
            <li>Generate scan reports, vulnerability assessments, and AI-powered analysis</li>
            <li>Detect configuration drift and trigger alarm notifications</li>
            <li>Send service notifications, security alerts, and onboarding emails</li>
            <li>Process subscription billing and payments through our payment processor, Stripe (we do not store full card numbers)</li>
            <li>Analyze and improve Service performance and reliability</li>
            <li>Enrich scan data with third-party intelligence (GreyNoise IP reputation, Certificate Transparency logs, RDAP registration data)</li>
            <li>Respond to support requests</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Data Sharing and Third-Party Services</h2>
          <p>We do NOT sell your personal information. We share data with the following categories of third parties:</p>
          <ul>
            <li><strong>Microsoft Azure</strong> — All platform data for cloud hosting, storage, and authentication (East US region)</li>
            <li><strong>Anthropic</strong> — Scan findings and technology data for AI-powered analysis and remediation (Claude API)</li>
            <li><strong>GreyNoise</strong> — IP addresses for reputation classification (24-hour cache)</li>
            <li><strong>Microsoft Graph API</strong> — Email addresses and notification content for email delivery (alerts and onboarding)</li>
            <li><strong>Google Analytics</strong> — Anonymized usage data for analytics and service improvement</li>
            <li><strong>Certificate Transparency Logs</strong> — Domain names for subdomain discovery (crt.sh)</li>
            <li><strong>RDAP Registries</strong> — Domain names for registration data lookup</li>
          </ul>
          <p>
            <strong>Legal requirements:</strong> We may disclose data when required by law, court order, or governmental
            authority. <strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Data Retention</h2>
          <ul>
            <li><strong>Scan result files:</strong> 90 days (30 days active → 30 days Cool tier → deletion via Azure Blob lifecycle)</li>
            <li><strong>Asset findings:</strong> Retained while asset is active. Deleted when asset is removed (hard delete across all resources)</li>
            <li><strong>Drift events &amp; baselines:</strong> Retained while asset is active. Deleted with asset removal</li>
            <li><strong>GreyNoise cache:</strong> 24 hours (automatically refreshed)</li>
            <li><strong>AI remediation cache:</strong> 7 days (cached per CVE/template + asset type)</li>
            <li><strong>User account data:</strong> Retained while account is active. Deleted within 90 days of account closure</li>
            <li><strong>Onboarding email records:</strong> Retained while account is active (tracks sent emails for deduplication)</li>
            <li><strong>Weekly/monthly reports:</strong> Retained per blob lifecycle policy (stored in Azure Blob Storage)</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. Data Security</h2>
          <ul>
            <li>Encryption at rest: Azure Storage encryption (AES-256)</li>
            <li>Encryption in transit: HTTPS/TLS for all connections</li>
            <li>Authentication: Microsoft Azure AD External ID (CIAM) with JWT tokens</li>
            <li>API key security: SHA-256 hashed storage, full key shown only once at creation</li>
            <li>Per-user data isolation: all scan data partitioned by user ID</li>
            <li>Access controls: role-based access via CIAM</li>
            <li>Scan data isolation: separate result IDs per scan type (discovery, vuln-scan, deep-security)</li>
            <li>No plaintext credentials stored</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. Cookies and Local Storage</h2>
          <p>We use minimal browser storage:</p>
          <ul>
            <li><strong>Local Storage — Theme preference:</strong> Persists UI theme selection (indefinite)</li>
            <li><strong>Local Storage — Tier cache:</strong> Caches subscription tier for performance (session)</li>
            <li><strong>Session Storage — JWT tokens:</strong> Authentication state (session)</li>
            <li><strong>Cookies — MSAL auth:</strong> Microsoft authentication library (session)</li>
          </ul>
          <p>
            We do NOT use third-party advertising cookies. Google Analytics uses its own cookies for aggregate analytics.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Your Rights</h2>
          <ul>
            <li>Access your personal data</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your data (data removed across all resources)</li>
            <li>Export your scan data (CSV/JSON export available in-platform)</li>
            <li>Opt out of onboarding emails (toggle in Settings)</li>
            <li>Opt out of marketing communications</li>
          </ul>
          <p>
            To exercise these rights, email <a href="mailto:support@driftalarm.com">support@driftalarm.com</a>.
            Response time: within 30 days.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. GDPR (European Users)</h2>
          <p><strong>Lawful basis for processing:</strong></p>
          <ul>
            <li><strong>Contract performance:</strong> providing scanning and monitoring services you requested</li>
            <li><strong>Legitimate interest:</strong> service improvement, security, analytics</li>
            <li><strong>Consent:</strong> onboarding email sequence (opt-out available)</li>
          </ul>
          <p>
            <strong>Data controller:</strong> DriftAlarm. <strong>Data processor:</strong> DriftAlarm processes
            scan data on behalf of you (the controller of the scanned assets).
          </p>
          <p>
            <strong>Data subject rights:</strong> access, rectification, erasure, restriction, portability, objection.
          </p>
          <p>
            <strong>Data Processing Agreement:</strong> available at <Link to="/dpa">our DPA page</Link> for customers
            requiring formal DPA documentation.
          </p>
          <p>
            <strong>International transfers:</strong> Data processed in Azure East US (United States); Standard
            Contractual Clauses available on request. <strong>Data Protection Officer:</strong> Contact{' '}
            <a href="mailto:support@driftalarm.com">support@driftalarm.com</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. CCPA (California Users)</h2>
          <ul>
            <li><strong>Categories of personal information collected:</strong> identifiers (email, name), internet activity (scan usage, IP address), commercial information (subscription tier)</li>
            <li><strong>We do NOT sell personal information</strong> — not now, not ever</li>
            <li><strong>Right to know:</strong> Request disclosure of data collected about you</li>
            <li><strong>Right to delete:</strong> Request deletion of your personal information</li>
            <li><strong>Right to opt out:</strong> No sale of data to opt out of</li>
            <li><strong>Non-discrimination:</strong> We will not discriminate against you for exercising your CCPA rights</li>
          </ul>
          <p>
            To exercise CCPA rights, email <a href="mailto:support@driftalarm.com">support@driftalarm.com</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Children's Privacy</h2>
          <p>
            The Service is not intended for users under 18 years of age. We do not knowingly collect information
            from children.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Material changes will be communicated via email
            and posted on the Service. We will provide at least 30 days' notice for material changes affecting
            data processing.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Contact Us</h2>
          <p>
            For privacy inquiries or to exercise your rights, contact us at{' '}
            <a href="mailto:support@driftalarm.com">support@driftalarm.com</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Related Policies</h2>
          <ul>
            <li><Link to="/terms">Terms of Service</Link></li>
            <li><Link to="/acceptable-use">Acceptable Use Policy</Link></li>
            <li><Link to="/dpa">Data Processing Agreement</Link></li>
          </ul>
        </section>
      </div>

      <PublicFooter />
    </div>
  );
}

export default Privacy;
